<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useDisplay } from 'vuetify';
import {
  dockPanelClampWidth,
  dockPanelDragWidth,
  dockPanelLimits,
  dockPanelReadWidth,
  dockPanelWriteWidth,
} from '../utils/right-dock-panel';

/**
 * A panel docked on the right edge of the app. It is a Vuetify navigation drawer, so it registers
 * with the Vuetify layout and VMain gets narrower. In the docked mode, the page never goes under
 * the panel.
 *
 * The left edge is a drag handle. The width stays in localStorage under `storageKey`. Below
 * `fullscreenBelow` pixels of viewport width, the panel covers the full width and shows a back
 * button in place of the drag handle.
 */
const open = defineModel<boolean>({ required: true });

const props = withDefaults(
  defineProps<{
    storageKey: string;
    defaultWidth?: number;
    minWidth?: number;
    maxWidth?: number;
    /**
     * The part of the viewport outside the panel keeps at least this width. It holds the page and
     * the other drawers of the host, for example a left sidebar.
     */
    minRestWidth?: number;
    fullscreenBelow?: number;
    title?: string;
    backLabel?: string;
    resizeLabel?: string;
  }>(),
  {
    defaultWidth: 400,
    minWidth: 320,
    maxWidth: 720,
    minRestWidth: 600,
    fullscreenBelow: 600,
    title: '',
    backLabel: 'Back',
    resizeLabel: 'Resize',
  },
);

const emit = defineEmits<{
  /**
   * The drawer ended a transition of its position or its width, or a drag ended. The host can
   * redraw its charts. One change can give more than one event.
   */
  resized: [];
}>();

const display = useDisplay();
// With the `ssr` option, Vuetify gives a width of 0 until the app is mounted.
const viewportWidth = computed(() => display.width.value || (typeof window === 'undefined' ? 0 : window.innerWidth));
const fullscreen = computed(() => viewportWidth.value > 0 && viewportWidth.value < props.fullscreenBelow);
const limits = computed(() => dockPanelLimits(props.minWidth, props.maxWidth, viewportWidth.value, props.minRestWidth));
const userLimits = computed(() => ({ min: props.minWidth, max: props.maxWidth }));

const storage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

// The width that the user chose. A narrow window shows a smaller width, but it keeps this value,
// so the panel gets its chosen width again when the window gets wider.
const userWidth = ref(props.defaultWidth);
onMounted(() => {
  userWidth.value = dockPanelReadWidth(storage(), props.storageKey, props.defaultWidth, userLimits.value);
});
const width = computed(() => dockPanelClampWidth(userWidth.value, limits.value));
const drawerWidth = computed(() => (fullscreen.value ? viewportWidth.value : width.value));

// The value comes from a drag or a key inside the viewport limits. The store keeps it with the
// static limits only, so a narrow window does not lower the chosen width.
const saveWidth = (value: number) => {
  userWidth.value = dockPanelWriteWidth(storage(), props.storageKey, value, userLimits.value);
};

// ── Drag handle ─────────────────────────────────────────────────────────────
const dragging = ref(false);
let dragStart: { x: number; width: number } | null = null;
// A click on the handle without a move must not store the shown width over the chosen width.
let dragMoved = false;

const onPointerMove = (event: PointerEvent) => {
  if (!dragStart) return;
  dragMoved = true;
  userWidth.value = dockPanelDragWidth(dragStart.width, dragStart.x, event.clientX, limits.value);
};

const endDrag = (): boolean => {
  if (!dragStart) return false;
  dragStart = null;
  dragging.value = false;
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', stopDrag);
  window.removeEventListener('pointercancel', stopDrag);
  return dragMoved;
};

const stopDrag = () => {
  if (!endDrag()) return;
  saveWidth(userWidth.value);
  // The drawer has no transition during a drag, so no transitionend follows.
  emit('resized');
};

const startDrag = (event: PointerEvent) => {
  if (event.button !== 0) return;
  event.preventDefault();
  // The capture keeps the pointer events on the handle when the pointer leaves the window or
  // goes over an iframe. Without it, the drag can miss its pointerup.
  (event.currentTarget as Element | null)?.setPointerCapture?.(event.pointerId);
  dragStart = { x: event.clientX, width: width.value };
  dragMoved = false;
  dragging.value = true;
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', stopDrag);
  window.addEventListener('pointercancel', stopDrag);
};

const KEY_STEP = 16;
const onHandleKey = (event: KeyboardEvent) => {
  const delta = event.key === 'ArrowLeft' ? KEY_STEP : event.key === 'ArrowRight' ? -KEY_STEP : 0;
  if (delta === 0) return;
  event.preventDefault();
  // The width transition of the drawer then gives the `resized` event.
  saveWidth(width.value + delta);
};

// An unmount during a drag removes the listeners and stores nothing.
onBeforeUnmount(endDrag);

// The drawer moves with `transform` when it opens or closes, and it animates `width`. A transition
// of an element inside the panel, or of another property, does not change the page width.
const onTransitionEnd = (event: TransitionEvent) => {
  if (event.target !== event.currentTarget) return;
  if (event.propertyName === 'transform' || event.propertyName === 'width') emit('resized');
};
</script>

<template>
  <v-navigation-drawer
    v-model="open"
    location="right"
    :width="drawerWidth"
    :temporary="fullscreen"
    :scrim="false"
    :mobile-breakpoint="0"
    touchless
    disable-resize-watcher
    class="right-dock-panel"
    :class="{
      'right-dock-panel--dragging': dragging,
      'right-dock-panel--fullscreen': fullscreen,
    }"
    @transitionend="onTransitionEnd"
  >
    <div
      v-if="!fullscreen"
      class="right-dock-panel__handle"
      role="separator"
      aria-orientation="vertical"
      :aria-label="resizeLabel"
      :aria-valuenow="width"
      :aria-valuemin="limits.min"
      :aria-valuemax="limits.max"
      tabindex="0"
      @pointerdown="startDrag"
      @lostpointercapture="stopDrag"
      @keydown="onHandleKey"
    />

    <div class="right-dock-panel__body">
      <header class="right-dock-panel__header">
        <v-btn
          v-if="fullscreen"
          icon="mdi-arrow-left"
          variant="text"
          size="small"
          :aria-label="backLabel"
          @click="open = false"
        />
        <slot name="header">
          <span class="right-dock-panel__title">{{ title }}</span>
        </slot>
      </header>

      <div class="right-dock-panel__content">
        <slot />
      </div>

      <footer
        v-if="$slots.footer"
        class="right-dock-panel__footer"
      >
        <slot name="footer" />
      </footer>
    </div>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
.right-dock-panel {
  border-left: 1px solid var(--color-border) !important;
  box-shadow: none !important;
  background: white;

  // The drawer animates its width. During a drag, the animation puts the edge behind the pointer.
  &--dragging {
    transition: none !important;
    user-select: none;
  }

  &--fullscreen {
    border-left: none !important;
  }

  :deep(.v-navigation-drawer__content) {
    display: flex;
    overflow: hidden;
  }

  &__handle {
    position: absolute;
    top: 0;
    bottom: 0;
    left: -3px;
    width: 6px;
    cursor: col-resize;
    z-index: 1;
    touch-action: none;

    &:hover,
    &:focus-visible {
      background: rgb(var(--v-theme-primary), 0.24);
      outline: none;
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 56px;
    padding: 8px 12px 8px 16px;
    border-bottom: 1px solid var(--color-border);
    flex-shrink: 0;
  }

  &__title {
    flex: 1;
    min-width: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--color-ds-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  &__footer {
    flex-shrink: 0;
    border-top: 1px solid var(--color-border);
  }
}
</style>
